import React from 'react';
import { Info, Calendar, User, Clock, CheckCircle2 } from 'lucide-react';
import Modal from "@/components/ui/Modal";
import { formatInstallationTrade } from '../../verve-teams/utils/teamMemberLabels';

export const TaskDetailsModal = ({ isOpen, onClose, task }) => {
  if (!isOpen || !task) return null;

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const modalTitle = (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
        <Info className="w-5 h-5 text-[#f97316]" />
      </div>
      <div className="flex flex-col">
        <h2 className="text-[16px] font-semibold text-slate-800 tracking-tight leading-none">Task Details</h2>
        <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mt-1.5">Project Overview</span>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      footer={
        <div className="flex justify-end w-full pt-2">
          <button
            onClick={onClose}
            className="px-10 py-3 bg-[#64748b] hover:bg-[#475569] text-white rounded-xl text-[12px] font-bold tracking-widest transition-all shadow-md shadow-blue-100 cursor-pointer"
          >
            Close
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-6 font-poppins py-2">
        {/* Task Name and Description - One below the other */}
        <div className="flex flex-col gap-5 p-6 rounded-2xl bg-slate-50/50 border border-slate-100">
           <div className="flex flex-col gap-2">
              <span className="text-[12px]   tracking-widest">Task Name</span>
              <h1 className="text-[12px]  leading-tight  tracking-tight">
                 {task.taskName}
              </h1>
           </div>

           <div className="flex flex-col gap-2 border-t border-slate-200/60 pt-5">
              <span className="text-[12px   tracking-widest">Description</span>
              <p className="text-[12px] text-slate-600  leading-relaxed">
                 {task.description || "No description provided."}
              </p>
           </div>
        </div>

        {/* Simplified Info Grid */}
        <div className="grid grid-cols-3 gap-4">
           <div className="flex flex-col gap-1.5 p-4 rounded-xl border border-slate-100 bg-white shadow-sm">
              <div className="flex items-center gap-2 text-slate-400">
                 <User className="w-3 h-3" />
                 <span className="text-[9px] font-bold uppercase tracking-widest">Assigned To</span>
              </div>
              <span className="text-[13px] font-bold text-slate-700 truncate">{task.assignedOfficer?.name || 'Unassigned'}</span>
           </div>
           <div className="flex flex-col gap-1.5 p-4 rounded-xl border border-slate-100 bg-white shadow-sm">
              <div className="flex items-center gap-2 text-slate-400">
                 <Calendar className="w-3 h-3" />
                 <span className="text-[9px] font-bold uppercase tracking-widest">Start Date</span>
              </div>
              <span className="text-[13px] font-bold text-slate-700">{formatDate(task.startDate)}</span>
           </div>
           <div className="flex flex-col gap-1.5 p-4 rounded-xl border border-slate-100 bg-white shadow-sm">
              <div className="flex items-center gap-2 text-slate-400">
                 <Calendar className="w-3 h-3" />
                 <span className="text-[9px] font-bold uppercase tracking-widest">Est. End Date</span>
              </div>
              <span className="text-[13px] font-bold text-slate-700">{formatDate(task.estimatedEndDate)}</span>
           </div>
        </div>

        {/* Status and Progress Bar */}
        <div className="flex flex-col gap-4 p-6 rounded-2xl border border-slate-100 bg-white shadow-sm">
           <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                 <span className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest border ${
                    task.status === 'COMPLETED' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-blue-50 text-blue-600 border-blue-100'
                 }`}>
                    {task.status?.replace('_', ' ')}
                 </span>
                 {task.installationTrade && (
                    <span className="px-3 py-1 rounded-lg bg-slate-50 text-slate-500 text-[10px] font-bold uppercase tracking-widest border border-slate-100">
                        {formatInstallationTrade(task.installationTrade)}
                    </span>
                 )}
              </div>
              <div className="flex flex-col items-end">
                <span className="text-[18px] font-bold text-[#2b448f] tabular-nums leading-none">{task.progress || 0}%</span>
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">Completion</span>
              </div>
           </div>
           <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                 className="h-full bg-[#2b448f] rounded-full transition-all duration-500 shadow-sm shadow-blue-100" 
                 style={{ width: `${task.progress || 0}%` }} 
              />
           </div>
        </div>
      </div>
    </Modal>
  );
};
